/**
 * ThemeSwitcher Component
 * Button to toggle between light and dark themes
 * Detects system preference on first load, then allows manual override
 */

import { Moon, Sun } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';

interface ThemeSwitcherProps {
  size?: 'compact' | 'default';
}

export function ThemeSwitcher({ size = 'default' }: ThemeSwitcherProps) {
  const [isDark, setIsDark] = useState(false);
  const [mounted, setMounted] = useState(false);

  const iconSize = size === 'compact' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const buttonSize = size === 'compact' ? 'h-7 w-7' : 'h-9 w-9';

  // Initialize theme on mount
  useEffect(() => {
    setMounted(true);
    const root = document.documentElement;
    
    // Check current theme state
    const dataMode = root.getAttribute('data-mode');
    const hasDarkClass = root.classList.contains('dark');
    const currentMode = dataMode === 'dark' || (dataMode === null && hasDarkClass) ? 'dark' : 'light';
    setIsDark(currentMode === 'dark');
  }, []);

  // Listen for theme changes (from system preference or other sources)
  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;
    
    // Function to update state based on DOM
    const updateThemeState = () => {
      const dataMode = root.getAttribute('data-mode');
      const hasDarkClass = root.classList.contains('dark');
      const currentMode = dataMode === 'dark' || (dataMode === null && hasDarkClass) ? 'dark' : 'light';
      setIsDark(currentMode === 'dark');
    };

    // Watch for attribute changes
    const observer = new MutationObserver(updateThemeState);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-mode', 'class'],
    });

    // Also listen for system preference changes (if no user preference is set)
    const savedMode = localStorage.getItem('theme-mode');
    if (!savedMode) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        updateThemeState();
      };
      
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => {
          mediaQuery.removeEventListener('change', handleChange);
          observer.disconnect();
        };
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => {
          mediaQuery.removeListener(handleChange);
          observer.disconnect();
        };
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [mounted]);

  const toggleTheme = () => {
    const root = document.documentElement;
    const currentMode = root.getAttribute('data-mode');
    const newMode = currentMode === 'dark' ? 'light' : 'dark';
    
    // Save user preference (this will prevent App.tsx from listening to system changes)
    localStorage.setItem('theme-mode', newMode);
    
    // Apply new mode
    root.setAttribute('data-mode', newMode);
    
    // Add/remove .dark class for Tailwind compatibility
    if (newMode === 'dark') {
      root.classList.add('dark');
      root.classList.add('dark-mode');
    } else {
      root.classList.remove('dark');
      root.classList.remove('dark-mode');
    }
    
    setIsDark(newMode === 'dark');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleTheme();
    }
  };

  // Don't render until mounted to avoid hydration mismatch
  if (!mounted) {
    return (
      <button
        className={`flex ${buttonSize} items-center justify-center rounded-lg transition-all hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2`}
        style={{
          background: 'var(--accent-gold)',
          color: 'white',
        }}
        aria-label="Theme switcher"
        disabled
      >
        <Sun className={iconSize} />
      </button>
    );
  }

  return (
    <button
      onClick={toggleTheme}
      onKeyDown={handleKeyDown}
      className={`flex ${buttonSize} items-center justify-center rounded-lg transition-all hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2`}
      style={{
        background: 'var(--accent-gold)',
        color: 'white',
      }}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? <Moon className={iconSize} /> : <Sun className={iconSize} />}
    </button>
  );
}

